import { Link } from "react-router-dom";
import { services } from "../../../mocks/services";
import { useReveal } from "../../../hooks/useReveal";

const editorialImages = [
  "https://public.readdy.ai/ai/img_res/0d8db2fa94148aae146114d37cc27664.jpg",
  "https://readdy.ai/api/search-image?query=cinematic%20wedding%20photography%20bride%20and%20groom%20elegant%20intimate%20moment%20golden%20hour%20soft%20light%20romantic%20editorial%20fine%20art%20luxury%20wedding%20portrait%20warm%20tones%20sophisticated%20styling%20beautiful%20couple%20emotional%20connection%20timeless%20photography%20premium%20quality%20with%20rich%20background%20depth%20and%20natural%20light&width=800&height=560&seq=pk-svc-wedding-v2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=beautiful%20maternity%20photography%20mother%20elegant%20flowing%20dress%20golden%20hour%20soft%20natural%20light%20close%20up%20baby%20bump%20hands%20tender%20emotional%20artistic%20fine%20art%20portrait%20premium%20studio%20quality%20warm%20neutral%20tones%20heirloom%20quality%20atmospheric%20beautiful%20anticipation&width=800&height=560&seq=pk-svc-milestones-v2&orientation=landscape",
];

export default function ServicesOverview() {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-pk-ivory py-28 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-8">

        {/* ── Section Header ─────────────────────────────────── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-18 gap-8">
          <div className="reveal">
            <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">
              Our Signature Sessions
            </span>
            <h2 className="font-display text-5xl md:text-6xl font-light text-pk-charcoal mt-4 leading-tight max-w-lg">
              Every Session is a<br />
              <em className="font-medium">Keepsake in the Making</em>
            </h2>
          </div>
          <p className="reveal delay-200 font-sans text-sm text-pk-muted leading-7 max-w-xs">
            Guided with care. Executed with artistry. Delivered as heirlooms your family will treasure for generations to come.
          </p>
        </div>

        {/* ── Editorial Asymmetric Grid ──────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">

          {/* Card 1 — Tall feature card */}
          <div className="reveal group md:col-span-5 relative overflow-hidden rounded-sm" style={{ transitionDelay: "0ms" }}>
            <div className="img-hover-zoom w-full h-[680px] overflow-hidden relative">
              <img
                src={editorialImages[0]}
                alt="Newborn photography San Antonio — Precious Keepsakes"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-px bg-pk-gold" />
                <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold">
                  {services[0].title}
                </span>
              </div>
              <h3 className="font-display text-3xl font-medium text-white leading-snug mb-3">
                {services[0].shortDesc}
              </h3>
              <div className="overflow-hidden max-h-0 group-hover:max-h-32 transition-all duration-500 ease-out">
                <ul className="space-y-1.5 mt-3 mb-4">
                  {services[0].features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2">
                      <i className="ri-checkbox-blank-circle-fill text-pk-gold" style={{ fontSize: "4px" }} />
                      <span className="font-sans text-xs text-white/70">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 font-sans text-xs tracking-widest2 uppercase text-white/70 hover:text-pk-gold transition-colors duration-300 cursor-pointer group/lnk whitespace-nowrap"
              >
                <span>Explore Session</span>
                <i className="ri-arrow-right-line group-hover/lnk:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>

          {/* Right column — 2 stacked cards */}
          <div className="md:col-span-7 flex flex-col gap-5">

            {/* Card 2 */}
            <div className="reveal group relative overflow-hidden rounded-sm flex-1" style={{ transitionDelay: "120ms" }}>
              <div className="img-hover-zoom w-full h-72 overflow-hidden relative">
                <img
                  src={editorialImages[1]}
                  alt="Wedding photography San Antonio — Precious Keepsakes"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
              {/* Horizontal layout overlay */}
              <div className="absolute inset-0 flex items-end p-8 md:items-center">
                <div className="md:max-w-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-px bg-pk-gold" />
                    <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold">{services[1].title}</span>
                  </div>
                  <h3 className="font-display text-2xl font-medium text-white leading-snug">{services[1].shortDesc}</h3>
                  <div className="overflow-hidden max-h-0 group-hover:max-h-20 transition-all duration-500">
                    <div className="pt-3">
                      <Link to="/services" className="inline-flex items-center gap-2 font-sans text-xs tracking-widest2 uppercase text-white/60 hover:text-pk-gold transition-colors duration-300 cursor-pointer whitespace-nowrap">
                        View Wedding Sessions <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="reveal group relative overflow-hidden rounded-sm flex-1" style={{ transitionDelay: "240ms" }}>
              <div className="img-hover-zoom w-full h-72 overflow-hidden relative">
                <img
                  src={editorialImages[2]}
                  alt="Maternity and milestone photography San Antonio"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
              <div className="absolute inset-0 flex items-end p-8 md:items-center">
                <div className="md:max-w-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-px bg-pk-gold" />
                    <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold">{services[2].title}</span>
                  </div>
                  <h3 className="font-display text-2xl font-medium text-white leading-snug">{services[2].shortDesc}</h3>
                  <div className="overflow-hidden max-h-0 group-hover:max-h-20 transition-all duration-500">
                    <div className="pt-3">
                      <Link to="/services" className="inline-flex items-center gap-2 font-sans text-xs tracking-widest2 uppercase text-white/60 hover:text-pk-gold transition-colors duration-300 cursor-pointer whitespace-nowrap">
                        View Milestone Sessions <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ── CTA row ───────────────────────────────────────── */}
        <div className="reveal mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-pk-light">
          <p className="font-display text-xl italic text-pk-charcoal/70 max-w-sm">
            &ldquo;She has a calming way of guiding you — then gracefully capturing the moment.&rdquo;
          </p>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a
              href="tel:2108154698"
              className="font-sans text-sm text-pk-muted hover:text-pk-gold transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              Call to discuss &rarr;
            </a>
            <Link
              to="/services"
              className="bg-pk-charcoal text-white font-sans text-xs tracking-widest3 uppercase px-10 py-4 rounded-sm hover:bg-pk-gold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              View All Sessions
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
